'use client'

import type { RelationshipFieldClientComponent } from 'payload'

import { FieldDescription, FieldError, FieldLabel, useField } from '@payloadcms/ui'
import { useEffect, useState } from 'react'

const DEFAULT_MAX_SELECTED = 4
const CAPTION_MAX_LENGTH = 60

type InstagramPostOption = {
  caption?: string | null
  id: number | string
  mediaUrl?: string | null
  thumbnailUrl?: string | null
}

function truncate(text: string, maxLength: number) {
  if (text.length <= maxLength) return text
  return `${text.slice(0, maxLength).trimEnd()}…`
}

export const InstagramPostsPicker: RelationshipFieldClientComponent = (props) => {
  const { field, path, readOnly } = props
  const { value, setValue } = useField<(number | string)[]>({ path })
  const [options, setOptions] = useState<InstagramPostOption[]>([])
  const [loading, setLoading] = useState(true)
  const maxSelected =
    (field.admin?.custom as { maxSelected?: number } | undefined)?.maxSelected ??
    DEFAULT_MAX_SELECTED

  useEffect(() => {
    let cancelled = false

    fetch('/api/instagram-posts?limit=100&depth=0&sort=-timestamp')
      .then((res) => res.json())
      .then((data) => {
        if (!cancelled) setOptions(data?.docs || [])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [])

  const selected = Array.isArray(value) ? value : []

  const toggle = (id: number | string) => {
    if (readOnly) return

    if (selected.includes(id)) {
      setValue(selected.filter((v) => v !== id))
      return
    }

    if (selected.length >= maxSelected) return

    setValue([...selected, id])
  }

  return (
    <div className="field-type" style={{ marginBottom: 'var(--base)' }}>
      <FieldLabel label={field.label} path={path} required={field.required} />
      <FieldDescription description={field.admin?.description} path={path} />

      <div style={{ fontSize: '13px', margin: '4px 0 8px', opacity: 0.75 }}>
        {selected.length}/{maxSelected} ausgewählt
      </div>

      {loading ? (
        <div>Lade Instagram-Posts…</div>
      ) : (
        <div
          style={{
            display: 'grid',
            gap: '12px',
            gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
            maxHeight: '460px',
            overflowY: 'auto',
            paddingRight: '4px',
          }}
        >
          {options.map((post) => {
            const isSelected = selected.includes(post.id)
            const isDisabled = readOnly || (!isSelected && selected.length >= maxSelected)
            const caption = truncate(post.caption || '', CAPTION_MAX_LENGTH)
            const image = post.thumbnailUrl || post.mediaUrl

            return (
              <button
                key={post.id}
                disabled={isDisabled}
                onClick={() => toggle(post.id)}
                style={{
                  background: 'var(--theme-elevation-0)',
                  border: isSelected
                    ? '2px solid var(--theme-success-500)'
                    : '1px solid var(--theme-elevation-150)',
                  borderRadius: '4px',
                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '6px',
                  opacity: isDisabled ? 0.5 : 1,
                  padding: '8px',
                  textAlign: 'left',
                }}
                type="button"
              >
                {image ? (
                  <img
                    alt=""
                    src={image}
                    style={{
                      aspectRatio: '1 / 1',
                      borderRadius: '4px',
                      objectFit: 'cover',
                      width: '100%',
                    }}
                  />
                ) : (
                  <div
                    style={{
                      alignItems: 'center',
                      aspectRatio: '1 / 1',
                      background: 'var(--theme-elevation-100)',
                      borderRadius: '4px',
                      display: 'flex',
                      justifyContent: 'center',
                      width: '100%',
                    }}
                  >
                    —
                  </div>
                )}
                <span style={{ fontSize: '12px' }}>{caption || '(kein Text)'}</span>
              </button>
            )
          })}
        </div>
      )}

      <FieldError path={path} />
    </div>
  )
}
